import { FilterMetadata, SortMeta } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';

enum FilterMatchMode {
  contains = 'LIKE',
  equals = 'EQ',
  in = 'IN',
  lt = 'LT',
  gt = 'GT',
}

enum SortOrder {
  desc = -1,
  asc = 1,
}

export class FilterQueryParams {
  first?: number | undefined | null;
  last?: number | undefined | null;
  rows?: number | undefined | null;
  sortField?: string | string[] | null | undefined;
  sortOrder?: number | undefined | null;
  filters?: {
    [s: string]: FilterMetadata | FilterMetadata[] | undefined;
  };
  globalFilter?: string | string[] | undefined | null;
  multiSortMeta?: SortMeta[] | undefined | null;

  queryParams: Array<{ [key: string]: unknown }> = [];
  queryParamsAsString = '&';

  constructor({
    first,
    last,
    sortField,
    sortOrder,
    filters,
    globalFilter,
    multiSortMeta,
    rows,
  }: TableLazyLoadEvent = {}) {
    this.first = first;
    this.last = last;
    this.sortField = sortField;
    this.sortOrder = sortOrder;
    this.filters = filters;
    this.globalFilter = globalFilter;
    this.multiSortMeta = multiSortMeta;
    this.rows = rows;

    this.buildQueryParams();
  }

  public toString(): string {
    const queryParamsToString = Object.entries(this.queryParams).map(([index, value]) => {
      return index && value !== null && value !== undefined
        ? `${encodeURIComponent(Object.keys(value)[0])}=${Object.values(value)[0]}`
        : '';
    });
    this.queryParamsAsString += queryParamsToString.join('&');
    this.queryParamsAsString = this.queryParamsAsString.startsWith('&')
      ? this.queryParamsAsString.replace('&', '?')
      : `?${this.queryParamsAsString}`;

    return this.queryParamsAsString;
  }

  private buildQueryParams() {
    this.setPaginationQueryParams();
    this.setSortingQueryParams();
    this.setFilterQueryParams();
  }

  private setPaginationQueryParams() {
    if (this.first !== null && this.first !== undefined && this.rows) {
      this.queryParams.push({ page: this.first / this.rows });
      this.queryParams.push({ size: this.rows });
    }
  }

  private setSortingQueryParams() {
    const primengSortList = this.multiSortMeta
      ? this.multiSortMeta
      : [
          {
            field: this.sortField,
            order: this.sortOrder,
          },
        ];

    primengSortList.map((value) => {
      if (value.field) {
        this.queryParams.push({ sort: `${value.field},${SortOrder[value.order ?? 1]}` });
      }
    });
  }

  private setFilterQueryParams() {
    for (const key in this.filters) {
      const complexFilter = (<FilterMetadata>this.filters[key])?.value;
      const queryFilter: { [key: string]: unknown } = {};
      if (complexFilter != null) {
        if (complexFilter instanceof Object && (complexFilter.min || complexFilter.max)) {
          if (complexFilter.min) {
            queryFilter[`filter[${key}][${FilterMatchMode['gt']}]`] = complexFilter.min;
            this.queryParams.push(queryFilter);
          }
          if (complexFilter.max) {
            queryFilter[`filter[${key}][${FilterMatchMode['lt']}]`] = complexFilter.max;
            this.queryParams.push(queryFilter);
          }
        } else if (complexFilter instanceof Object && (complexFilter.startDate || complexFilter.endDate)) {
          if (complexFilter.startDate) {
            queryFilter[`filter[${key}][${FilterMatchMode['gt']}]`] = complexFilter.startDate;
            this.queryParams.push(queryFilter);
          }
          if (complexFilter.endDate) {
            queryFilter[`filter[${key}][${FilterMatchMode['lt']}]`] = complexFilter.endDate;
            this.queryParams.push(queryFilter);
          }
        } else {
          const matchModeKey =
            typeof (<FilterMetadata>this.filters[key]).value === 'boolean'
              ? 'equals'
              : (<FilterMetadata>this.filters[key])?.matchMode ?? 'contains';
          const matchMode =
            FilterMatchMode[matchModeKey as keyof typeof FilterMatchMode] ?? FilterMatchMode['contains'];

          if (Array.isArray((<FilterMetadata>this.filters[key])?.value)) {
            (<FilterMetadata>this.filters[key])?.value.map((item: unknown) => {
              queryFilter[`filter[${key}][${matchMode}]`] = item;
              this.queryParams.push(queryFilter);
            });
          } else {
            queryFilter[`filter[${key}][${matchMode}]`] = (<FilterMetadata>this.filters[key])?.value;
            this.queryParams.push(queryFilter);
          }
        }
      }
    }
  }
}

export const defaultPageSize = 20;
